import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { sv } from "date-fns/locale";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { REGISTER_CARD_MODAL } from "../../redux/reducers/cardReducer";
import { validateDateFormat } from "../../util/validators";
import { NavLink, Navigate } from "react-router";
import { isPrivileged } from "../../util/PrivilegeUtil";
import { addStaticTimeToDate } from "../../util/formats";
import { GET_CARD_TYPE_CONFIGURATIONS } from "../../redux/reducers/cardTypeReducer";
import { isCorrectCardNumber } from "../../util/validators";

export function RegisterCard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const carePrincipalId = useSelector(
    (store: any) => store.session.selectedCarePrincipal.id
  );
  const currentCard = useSelector(
    (store: any) => store.card.cardWithHistory.current
  );

  const [showCardId, setShowCardId] = useState(true);
  const currentUser = useSelector((store: any) => store.session.currentUser);
  const [preliminaryDate, setPreliminaryDate] = useState(new Date());
  const [endDate, setEndDate] = useState<Date>(
    new Date(new Date().setDate(new Date().getDate() + 364))
  );
  const [preliminaryDateError, setPreliminaryDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);
  const reasons = getReasons();

  const searchForm = useSelector((store: any) => store.card.params);
  const cardTypeConfigs = useSelector((state: any) => state.cardTypes.cardTypeConfig);
  const selctedConfig = cardTypeConfigs?.find((x: { cardType: any }) =>  x.cardType.id === searchForm.cardTypeId);

  useEffect(() => {
    dispatch({ type: GET_CARD_TYPE_CONFIGURATIONS, carePrincipalId});
  }, [])

  function getReasons() {
    let reasons = [{ id: "VALID_CARD", name: "Personen har frikort" }];
    if (isPrivileged(currentUser.privileges, "EFK.CARD.ADD.MEDICAL", "WRITE")) {
      reasons.push({ id: "MEDICAL", name: "Medicinska skäl" });
    }
    return reasons;
  }



  const setType = (values: any) => {
    if (values.target.value === "MEDICAL") {
      setShowCardId(false);
    } else {
      setShowCardId(true);
    }
  };

  const handleStartDate = () => {
    let startDate = new Date(endDate);
    startDate = new Date(startDate.setMonth(endDate.getMonth() - selctedConfig.costLimitPeriod));
    if (endDate.getMonth() !== 1 || endDate.getDate() !== 29 || selctedConfig.costLimitPeriod %12 !== 0) {
      startDate.setDate(startDate.getDate() +1);
    }
    return startDate;
  }

  const handlePreliminaryDateChange = (date: any) => {
    if (date != null && validateDateFormat(date)) {
      setPreliminaryDate(date);
      setPreliminaryDateError(false);
    } else {
      setPreliminaryDate(date);
      setPreliminaryDateError(true);
    }
  };

  const handleEndDateChange = (date: any) => {
    if (date != null && validateDateFormat(date)) {
      setEndDate(date);
      setEndDateError(false);
    } else {
      setEndDate(date);
      setEndDateError(true);
    }
  };

  const onSubmit = (values: any) => {
    if (!preliminaryDateError && !endDateError) {
      const registerCardForm = {
        carePrincipalId: carePrincipalId,
        personId: searchForm.personId,
        cardTypeId: searchForm.cardTypeId,
        startDate: addStaticTimeToDate(handleStartDate()),
        preliminaryCardDate: addStaticTimeToDate(preliminaryDate),
        endDate: addStaticTimeToDate(endDate),
        cardId: showCardId === true ? values.cardId : null,
        extendExisting: currentCard ? true : false,
        registrationReason: values.registrationReason,
      };
      dispatch({ type: REGISTER_CARD_MODAL, registerCardForm });
      return <Navigate to="/search-card" />;
    }
  };

  return (
    <>
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h4">
          {t("searchCard.menu.registerCard")}
        </Typography>
        <Stack spacing={2}>
          <Stack spacing={4} direction="row">
            <Stack>
              <Typography fontWeight="bold">
                {t("searchCard.form.personIdLabel")}
              </Typography>
              <Typography>{searchForm.personId}</Typography>
            </Stack>
            <Stack>
              <Typography fontWeight="bold">
                {t("searchCard.cardInfo.cardType")}
              </Typography>
              <Typography>{t(searchForm.cardTypeId)}</Typography>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Stack direction="column">
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={sv}>
                <DatePicker
                  label={t("validFrom")}
                  value={preliminaryDate}
                  onChange={handlePreliminaryDateChange}
                  renderInput={(params: any) => (
                    <TextField
                      required={true}
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: t("dateFormat"),
                      }}
                      error={preliminaryDateError}
                    />
                  )}
                />
              </LocalizationProvider>
              {preliminaryDateError ? (
                <Typography
                  variant="body2"
                  sx={{ color: "#DC143C" }}
                  role="alert"
                >
                  {t("errors.invalidDate")}
                </Typography>
              ) : null}
            </Stack>
            <Stack direction="column">
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={sv}>
                <DatePicker
                  label={t("validTo")}
                  value={endDate}
                  onChange={handleEndDateChange}
                  renderInput={(params: any) => (
                    <TextField
                      required={true}
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: t("dateFormat"),
                      }}
                      error={endDateError}
                    />
                  )}
                />
              </LocalizationProvider>
              {endDateError ? (
                <Typography
                  variant="body2"
                  sx={{ color: "#DC143C" }}
                  role="alert"
                >
                  {t("errors.invalidDate")}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          <Controller
            name="registrationReason"
            control={control}
            render={({ field: { value = reasons[0].id } }) => (
              <TextField
                select
                label={t("searchCard.registerCard.cardReason") + " *"}
                value={value}
                sx={{ width: "22ch", textAlign: "left" }}
                error={errors.registrationReason ? true : false}
                {...register("registrationReason", {
                  required: true,
                  onChange: (e) => setType(e),
                })}
              >
                {reasons
                  ? reasons.map((reason: any, i: number) => (
                      <MenuItem key={i} id={reason.id} value={reason.id}>
                        {reason.name}
                      </MenuItem>
                    ))
                  : null}
              </TextField>
            )}
          />
          {showCardId && (
            <Box>
              <Controller
                name="cardId"
                key="cardId"
                control={control}
                render={() => (
                  <TextField
                    id="cardId"
                    error={errors.cardId ? true : false}
                    variant="standard"
                    label={t("searchCard.cardInfo.cardId") + " *"}
                    {...register("cardId", {
                      required: true,
                      validate:
                        isCorrectCardNumber,
                    })}
                  />
                )}
              />
              {errors.cardId && errors.cardId.type === "required" && (
                <Typography
                  variant="body2"
                  sx={{ color: "#DC143C" }}
                  role="alert"
                >
                  {t("errors.emptyField")}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
        <Button
          sx={{ marginTop: "1vh" }}
          color="success"
          size="small"
          type="submit"
          variant="contained"
        >
          {t("save")}
        </Button>
        <Stack>
          <Button
            sx={{ marginTop: "1.6rem", maxWidth: "6rem" }}
            component={NavLink}
            to={"/search-card"}
            variant="contained"
          >
            {t("back")}
          </Button>
      </Stack>
      </Box>
    </>
  );
}
